import {
  deriveOfferCampaignStatus,
  parsePublicLiveOfferCampaignPayload,
  parsePublicOfferCampaignPagePayload,
} from '../lib/offer-campaigns';
import type { SupabaseClient } from '../supabase/client';
import type { Database, Json } from '../supabase/database.types';
import type {
  CreateOfferCampaignInput,
  DeleteOfferCampaignInput,
  OfferCampaign,
  OfferCampaignErrorKey,
  OfferCampaignItemWithSellable,
  OfferCampaignSellableOption,
  OfferCampaignWithItems,
  PublicLiveOfferCampaignSummary,
  PublicOfferCampaignPage,
  UpdateOfferCampaignInput,
} from '../types/offer-campaign';

export interface ListOfferCampaignSellablesArgs {
  readonly store_id: string;
  readonly search?: string | null;
  readonly category_id?: string | null;
  readonly limit?: number;
}

export interface PublicOfferCampaignReadOptions {
  readonly supabaseUrl: string;
}

export interface OfferCampaignService {
  listStoreOfferCampaigns(
    client: SupabaseClient,
    storeId: string,
  ): Promise<OfferCampaignWithItems[]>;
  getStoreOfferCampaign(
    client: SupabaseClient,
    args: { readonly store_id: string; readonly campaign_id: string },
  ): Promise<OfferCampaignWithItems | null>;
  listOfferCampaignSellables(
    client: SupabaseClient,
    args: ListOfferCampaignSellablesArgs,
  ): Promise<OfferCampaignSellableOption[]>;
  createOfferCampaign(
    client: SupabaseClient,
    input: CreateOfferCampaignInput,
  ): Promise<OfferCampaignWithItems>;
  updateOfferCampaign(
    client: SupabaseClient,
    input: UpdateOfferCampaignInput,
  ): Promise<OfferCampaignWithItems>;
  softDeleteOfferCampaign(client: SupabaseClient, input: DeleteOfferCampaignInput): Promise<void>;
  getPublicLiveOfferCampaign(
    client: SupabaseClient,
    storeSlug: string,
    options: PublicOfferCampaignReadOptions,
  ): Promise<PublicLiveOfferCampaignSummary | null>;
  getPublicOfferCampaign(
    client: SupabaseClient,
    args: { readonly storeSlug: string; readonly campaignSlug: string },
    options: PublicOfferCampaignReadOptions,
  ): Promise<PublicOfferCampaignPage>;
  mapOfferCampaignError(err: unknown): OfferCampaignErrorKey;
}

const VARIANT_ASSIGNMENTS_SELECT =
  'assignments:product_variant_value_assignments(product_variant_options(name, display_order), product_variant_option_values(value, display_order))';
const CAMPAIGN_SELECT = `*, offer_campaign_items(*, products(name, published, store_categories(name)), product_variants(price_cents, sale_price_cents, currency, inventory, active, is_default, ${VARIANT_ASSIGNMENTS_SELECT}))`;
const SELLABLES_SELECT = `id, product_id, price_cents, sale_price_cents, currency, inventory, active, is_default, products(id, name, published, store_categories(id, name)), ${VARIANT_ASSIGNMENTS_SELECT}`;

type SupabaseErrorLike = {
  readonly code?: string;
  readonly message?: string;
  readonly name?: string;
  readonly statusCode?: string | number;
  readonly constraint?: string;
  readonly details?: string;
};

function collectErrorStrings(value: unknown, depth = 0): string[] {
  if (depth > 3 || value === null || value === undefined) return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'number') return [String(value)];
  if (value instanceof Error) return [value.name, value.message];
  if (typeof value !== 'object') return [];

  const record = value as Record<string, unknown>;
  const keys = [
    'code',
    'statusCode',
    'name',
    'message',
    'details',
    'hint',
    'description',
    'error',
  ] as const;

  return keys.flatMap((key) => collectErrorStrings(record[key], depth + 1));
}

type QueryResponse<T> = {
  readonly data: T | null;
  readonly error: SupabaseErrorLike | null;
};

type QueryLike<T> = PromiseLike<QueryResponse<T>> & {
  select(columns?: string): QueryLike<T>;
  eq(column: string, value: unknown): QueryLike<T>;
  ilike(column: string, value: string): QueryLike<T>;
  is(column: string, value: unknown): QueryLike<T>;
  not(column: string, operator: string, value: unknown): QueryLike<T>;
  gt(column: string, value: unknown): QueryLike<T>;
  limit(count: number): QueryLike<T>;
  order(column: string, options?: { readonly ascending?: boolean }): QueryLike<T>;
  maybeSingle(): PromiseLike<QueryResponse<T>>;
};

interface OfferCampaignRow extends OfferCampaign {
  readonly offer_campaign_items?: readonly OfferCampaignItemJoinRow[];
}

interface OfferCampaignItemJoinRow {
  readonly id: string;
  readonly campaign_id: string;
  readonly store_id: string;
  readonly product_id: string;
  readonly variant_id: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly products?: {
    readonly name?: string | null;
    readonly published?: boolean | null;
    readonly store_categories?: { readonly name?: string | null } | null;
  } | null;
  readonly product_variants?: {
    readonly price_cents?: number | null;
    readonly sale_price_cents?: number | null;
    readonly currency?: string | null;
    readonly inventory?: number | null;
    readonly active?: boolean | null;
    readonly is_default?: boolean | null;
    readonly assignments?: readonly VariantOptionAssignmentJoinRow[] | null;
  } | null;
}

interface VariantOptionAssignmentJoinRow {
  readonly product_variant_options?: {
    readonly name?: string | null;
    readonly display_order?: number | null;
  } | null;
  readonly product_variant_option_values?: {
    readonly value?: string | null;
    readonly display_order?: number | null;
  } | null;
}

interface SellableJoinRow {
  readonly id: string;
  readonly product_id: string;
  readonly price_cents: number;
  readonly sale_price_cents: number | null;
  readonly currency: string;
  readonly inventory: number;
  readonly active: boolean;
  readonly is_default: boolean;
  readonly products?: {
    readonly id?: string | null;
    readonly name?: string | null;
    readonly published?: boolean | null;
    readonly store_categories?: {
      readonly id?: string | null;
      readonly name?: string | null;
    } | null;
  } | null;
  readonly assignments?: readonly VariantOptionAssignmentJoinRow[] | null;
}

function table(client: SupabaseClient, relation: string): QueryLike<unknown> {
  return (client.from as unknown as (name: string) => QueryLike<unknown>)(relation);
}

type PublicFunctions = Database['public']['Functions'];

/** The RPCs this service calls. Their arguments are checked against the generated types. */
type OfferCampaignRpcName =
  | 'create_offer_campaign'
  | 'update_offer_campaign'
  | 'soft_delete_offer_campaign'
  | 'get_public_live_offer_campaign'
  | 'get_public_offer_campaign';

type OfferCampaignRpc = <N extends OfferCampaignRpcName>(
  fn: N,
  args: PublicFunctions[N]['Args'],
) => PromiseLike<QueryResponse<unknown>>;

/** The runtime shape of `SupabaseClient.rpc`, without its generic signature. */
interface UntypedRpcClient {
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<QueryResponse<unknown>>;
}

/**
 * A typed RPC call that never instantiates supabase-js's generic `rpc`
 * signature. That signature is resolved over every generated function, and
 * `client.rpc.bind(client)` made TypeScript instantiate it in full: once the
 * generated map grew, that was TS2589 ("excessively deep"). Names and
 * arguments are typed from the generated map instead; the call keeps
 * `client` as `this`.
 */
function rpc(client: SupabaseClient): OfferCampaignRpc {
  const untyped = client as unknown as UntypedRpcClient;
  return (fn, args) => untyped.rpc(fn, args);
}

function toCampaignPayload(input: CreateOfferCampaignInput | UpdateOfferCampaignInput): {
  readonly [key: string]: Json;
} {
  return {
    store_id: input.store_id,
    title: input.title,
    short_description: input.short_description,
    starts_at: input.starts_at,
    ends_at: input.ends_at,
    is_active: input.is_active,
  };
}

function toCampaign(row: OfferCampaignRow): OfferCampaignWithItems {
  return {
    id: row.id,
    store_id: row.store_id,
    public_slug: row.public_slug,
    title: row.title,
    short_description: row.short_description,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    is_active: row.is_active,
    created_at: row.created_at,
    updated_at: row.updated_at,
    deleted_at: row.deleted_at,
    status: deriveOfferCampaignStatus(row),
    items: (row.offer_campaign_items ?? []).map(toCampaignItem),
  };
}

function toCampaignItem(row: OfferCampaignItemJoinRow): OfferCampaignItemWithSellable {
  const product = row.products;
  const variant = row.product_variants;
  return {
    id: row.id,
    campaign_id: row.campaign_id,
    store_id: row.store_id,
    product_id: row.product_id,
    variant_id: row.variant_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
    deleted_at: row.deleted_at,
    product_name: product?.name ?? '',
    category_name: product?.store_categories?.name ?? null,
    variant_option_summary: readVariantOptionSummary(variant?.assignments),
    variant_price_cents: variant?.price_cents ?? 0,
    variant_sale_price_cents: variant?.sale_price_cents ?? null,
    currency: 'ILS',
    available: variant?.inventory ?? 0,
    active: variant?.active ?? false,
    product_published: product?.published ?? false,
    is_default_variant: variant?.is_default ?? false,
  };
}

function toSellable(row: SellableJoinRow): OfferCampaignSellableOption {
  const product = row.products;
  const active = row.active;
  const productPublished = product?.published ?? false;
  const available = row.inventory;
  return {
    product_id: row.product_id,
    variant_id: row.id,
    product_name: product?.name ?? '',
    category_id: product?.store_categories?.id ?? null,
    category_name: product?.store_categories?.name ?? null,
    option_summary: readVariantOptionSummary(row.assignments),
    is_default_variant: row.is_default,
    price_cents: row.price_cents,
    sale_price_cents: row.sale_price_cents ?? 0,
    currency: 'ILS',
    available,
    active,
    product_published: productPublished,
    availability_state: !active
      ? 'disabled'
      : available <= 0
        ? 'out_of_stock'
        : available <= 5
          ? 'low_stock'
          : 'available',
    isSelectable:
      active &&
      productPublished &&
      available > 0 &&
      row.sale_price_cents !== null &&
      row.sale_price_cents > 0 &&
      row.sale_price_cents < row.price_cents,
  };
}

function readVariantOptionSummary(
  assignments: readonly VariantOptionAssignmentJoinRow[] | null | undefined,
): string {
  const summary = [...(assignments ?? [])]
    .sort((a, b) => {
      const optionOrderA = a.product_variant_options?.display_order ?? 0;
      const optionOrderB = b.product_variant_options?.display_order ?? 0;
      const valueOrderA = a.product_variant_option_values?.display_order ?? 0;
      const valueOrderB = b.product_variant_option_values?.display_order ?? 0;
      const valueA = a.product_variant_option_values?.value ?? '';
      const valueB = b.product_variant_option_values?.value ?? '';
      return (
        optionOrderA - optionOrderB || valueOrderA - valueOrderB || valueA.localeCompare(valueB)
      );
    })
    .map((assignment) => {
      const optionName = assignment.product_variant_options?.name?.trim() ?? '';
      const value = assignment.product_variant_option_values?.value?.trim() ?? '';
      return optionName && value ? `${optionName}: ${value}` : '';
    })
    .filter(Boolean)
    .join(' / ');

  return summary.slice(0, 120);
}

async function hydrateMutationResult(
  client: SupabaseClient,
  row: unknown,
): Promise<OfferCampaignWithItems> {
  const campaign = row as Partial<OfferCampaign> | null;
  if (!campaign?.id || !campaign.store_id) {
    throw { code: 'P0002', message: 'Campaign mutation returned no row' };
  }
  const hydrated = await getStoreOfferCampaign(client, {
    store_id: campaign.store_id,
    campaign_id: campaign.id,
  });
  if (!hydrated) {
    throw { code: 'P0002', message: 'Campaign hydration failed after mutation' };
  }
  return hydrated;
}

export async function listStoreOfferCampaigns(
  client: SupabaseClient,
  storeId: string,
): Promise<OfferCampaignWithItems[]> {
  const { data, error } = await table(client, 'offer_campaigns')
    .select(CAMPAIGN_SELECT)
    .eq('store_id', storeId)
    .is('deleted_at', null)
    .order('updated_at', { ascending: false })
    .order('id', { ascending: false });
  if (error) throw error;
  return ((data as readonly OfferCampaignRow[] | null) ?? []).map(toCampaign);
}

export async function getStoreOfferCampaign(
  client: SupabaseClient,
  args: { readonly store_id: string; readonly campaign_id: string },
): Promise<OfferCampaignWithItems | null> {
  const { data, error } = await table(client, 'offer_campaigns')
    .select(CAMPAIGN_SELECT)
    .eq('store_id', args.store_id)
    .eq('id', args.campaign_id)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return data ? toCampaign(data as OfferCampaignRow) : null;
}

export async function listOfferCampaignSellables(
  client: SupabaseClient,
  args: ListOfferCampaignSellablesArgs,
): Promise<OfferCampaignSellableOption[]> {
  const search = args.search?.trim() ?? '';
  let query = table(client, 'product_variants')
    .select(SELLABLES_SELECT)
    .eq('store_id_cache', args.store_id)
    .is('deleted_at', null)
    .eq('active', true)
    .not('sale_price_cents', 'is', null)
    .gt('sale_price_cents', 0)
    .order('created_at', { ascending: false })
    .limit(Math.min(Math.max(args.limit ?? 50, 1), 100));

  if (search) query = query.ilike('products.name', `%${search}%`);
  if (args.category_id) query = query.eq('products.category_id', args.category_id);

  const { data, error } = await query;
  if (error) throw error;
  return ((data as readonly SellableJoinRow[] | null) ?? [])
    .map(toSellable)
    .filter((item) => item.isSelectable);
}

export async function createOfferCampaign(
  client: SupabaseClient,
  input: CreateOfferCampaignInput,
): Promise<OfferCampaignWithItems> {
  const { data, error } = await rpc(client)('create_offer_campaign', {
    campaign_payload: toCampaignPayload(input),
    variant_ids: [...input.variant_ids],
  });
  if (error) throw error;
  return hydrateMutationResult(client, data);
}

export async function updateOfferCampaign(
  client: SupabaseClient,
  input: UpdateOfferCampaignInput,
): Promise<OfferCampaignWithItems> {
  const { data, error } = await rpc(client)('update_offer_campaign', {
    target_campaign_id: input.campaign_id,
    campaign_payload: toCampaignPayload(input),
    variant_ids: [...input.variant_ids],
  });
  if (error) throw error;
  return hydrateMutationResult(client, data);
}

export async function softDeleteOfferCampaign(
  client: SupabaseClient,
  input: DeleteOfferCampaignInput,
): Promise<void> {
  const { error } = await rpc(client)('soft_delete_offer_campaign', {
    target_campaign_id: input.campaign_id,
  });
  if (error) throw error;
}

export async function getPublicLiveOfferCampaign(
  client: SupabaseClient,
  storeSlug: string,
  options: PublicOfferCampaignReadOptions,
): Promise<PublicLiveOfferCampaignSummary | null> {
  const normalizedStoreSlug = storeSlug.trim();
  if (!normalizedStoreSlug) return null;
  const { data, error } = await rpc(client)('get_public_live_offer_campaign', {
    target_store_slug: normalizedStoreSlug,
  });
  if (error) throw error;
  return parsePublicLiveOfferCampaignPayload(data, options.supabaseUrl);
}

export async function getPublicOfferCampaign(
  client: SupabaseClient,
  args: { readonly storeSlug: string; readonly campaignSlug: string },
  options: PublicOfferCampaignReadOptions,
): Promise<PublicOfferCampaignPage> {
  const storeSlug = args.storeSlug.trim();
  const campaignSlug = args.campaignSlug.trim();
  if (!storeSlug || !campaignSlug) return { state: 'unavailable' };
  const { data, error } = await rpc(client)('get_public_offer_campaign', {
    target_store_slug: storeSlug,
    target_campaign_slug: campaignSlug,
  });
  if (error) throw error;
  return parsePublicOfferCampaignPagePayload(data, options.supabaseUrl);
}

export function mapOfferCampaignError(err: unknown): OfferCampaignErrorKey {
  const parts = collectErrorStrings(err);
  const haystack = parts.join(' ').toLowerCase();
  const codes = new Set(parts.map((part) => part.trim()));

  if (codes.has('P0560') || haystack.includes('empty campaign selection')) {
    return 'admin.offerCampaigns.errors.emptySelection';
  }
  if (codes.has('P0562') || haystack.includes('ineligible campaign sellable')) {
    return 'admin.offerCampaigns.errors.ineligibleSellable';
  }
  if (codes.has('P0566') || haystack.includes('too many campaign items')) {
    return 'admin.offerCampaigns.errors.itemLimitExceeded';
  }
  if (codes.has('P0567') || haystack.includes('duplicate campaign sellable')) {
    return 'admin.offerCampaigns.errors.duplicateSelection';
  }
  if (codes.has('P0601') || haystack.includes('billing entitlement required')) {
    return 'admin.entitlements.promotionKitBlocked';
  }
  if (
    codes.has('P0002') ||
    codes.has('PGRST116') ||
    haystack.includes('campaign hydration failed') ||
    haystack.includes('offer campaign not found') ||
    haystack.includes('0 rows')
  ) {
    return 'admin.offerCampaigns.errors.notFound';
  }
  if (
    codes.has('42501') ||
    haystack.includes('permission denied') ||
    haystack.includes('insufficient privilege') ||
    haystack.includes('not authorized')
  ) {
    return 'admin.offerCampaigns.errors.permissionDenied';
  }
  if (codes.has('23514') && haystack.includes('offer_campaigns_window_chk')) {
    return 'admin.offerCampaigns.errors.invalidWindow';
  }
  if (codes.has('23514') && haystack.includes('offer_campaigns_title_length_chk')) {
    return 'admin.offerCampaigns.errors.titleRequired';
  }
  if (codes.has('23514') && haystack.includes('offer_campaigns_description_length_chk')) {
    return 'admin.offerCampaigns.errors.descriptionTooLong';
  }
  if (codes.has('23514') && haystack.includes('offer_campaigns_no_bidi_override_chk')) {
    return 'admin.offerCampaigns.errors.titleInvalid';
  }
  if (
    haystack.includes('failed to fetch') ||
    haystack.includes('networkerror') ||
    haystack.includes('load failed')
  ) {
    return 'common.errors.networkFailed';
  }
  return 'common.errors.unknown';
}
